import React, { useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import "./Catalog.css";
import TaskList from "../../components/TaskList/TaskList";
import * as XLSX from "xlsx";
import { TaskService } from "../../services/task/taskService";
import { HelenService } from "../../services/helen/helenService";
import { TeamMembersService } from "../../services/teamMembers/teamMembersService";
import { useToast } from "../../components/common/ToastManager/ToastContext";
import Loader from "../../components/common/Loader/Loader";
import TopBar from "../../components/TopBar/TopBar";
import { TaskGroup } from "../Task/Task";
import { getUserId, getUserRole } from "../../utils/tokenUtils";
import { useNavigate } from "react-router-dom";
import Tooltip from "../../components/Tooltip";
import FieldMappingModal from "./FieldMappingModal";
import OptimizelyLogo from "../apps/icons/OptimizelyLogo";

// ─── Bulk Guide Modal ─────────────────────────────────────────────────────────

const GUIDE_SLIDES = [
  {
    step: 1,
    label: "Step 1 of 2",
    title: "Select Products & Click Download",
    image: "/images/guide-step1.svg",
  },
  {
    step: 2,
    label: "Step 2 of 2",
    title: "Export with Required Fields",
    image: "/images/guide-step2.svg",
  },
];

const BulkGuideModal: React.FC<{ isOpen: boolean; onClose: () => void }> = ({ isOpen, onClose }) => {
  const [slide, setSlide] = useState(0);

  useEffect(() => {
    if (isOpen) setSlide(0);
  }, [isOpen]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight" && slide < GUIDE_SLIDES.length - 1) setSlide((s) => s + 1);
      if (e.key === "ArrowLeft" && slide > 0) setSlide((s) => s - 1);
    };
    if (isOpen) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, slide, onClose]);

  if (!isOpen) return null;

  const current = GUIDE_SLIDES[slide];
  const isLast = slide === GUIDE_SLIDES.length - 1;

  return ReactDOM.createPortal(
    <div className="guide-overlay" onClick={onClose}>
      <div className="guide-modal" onClick={(e) => e.stopPropagation()}>
        <div className="guide-modal-header">
          <h3 className="guide-modal-title">How to Generate the Input Excel</h3>
          <button className="guide-modal-close" onClick={onClose} aria-label="Close">×</button>
        </div>

        <div className="guide-slide">
          <span className="guide-step-badge">
            <span className="guide-step-number">{current.step}</span>
            {current.label}
          </span>
          <h4 className="guide-slide-title">{current.title}</h4>
          <img
            src={current.image}
            alt={current.title}
            className="guide-slide-img"
          />
        </div>

        <div className="guide-footer">
          <div className="guide-dots">
            {GUIDE_SLIDES.map((_, i) => (
              <button
                key={i}
                className={`guide-dot${slide === i ? " active" : ""}`}
                onClick={() => setSlide(i)}
                aria-label={`Slide ${i + 1}`}
              />
            ))}
          </div>
          <div className="guide-nav">
            {slide > 0 && (
              <button className="guide-nav-btn prev" onClick={() => setSlide((s) => s - 1)}>
                ← Back
              </button>
            )}
            {isLast ? (
              <button className="guide-nav-btn next" onClick={onClose}>
                Got it ✓
              </button>
            ) : (
              <button className="guide-nav-btn next" onClick={() => setSlide((s) => s + 1)}>
                Next →
              </button>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

// ─── Bulk Upload Modal ────────────────────────────────────────────────────────

interface BulkUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedHelen: any;
  defaultBrandFilename: string | null;
  onSuccess: () => void;
}

const BulkUploadModal: React.FC<BulkUploadModalProps> = ({
  isOpen,
  onClose,
  selectedHelen,
  defaultBrandFilename,
  onSuccess,
}) => {
  const [errorMessage, setErrorMessage] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [isGuideOpen, setIsGuideOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { showToast } = useToast();

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") handleClose();
    };
    if (isOpen) window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen]);

  const handleClose = () => {
    setErrorMessage("");
    onClose();
  };

  const downloadTemplate = () => {
    const link = document.createElement("a");
    link.href = "/Optimziely_BulkTaskTemplate.xlsx";
    link.download = "Optimziely_BulkTaskTemplate.xlsx";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    setErrorMessage("");

    if (!file) {
      setErrorMessage("Please upload a valid Excel file.");
      return;
    }

    const validTypes = [
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "application/vnd.ms-excel",
    ];
    if (!validTypes.includes(file.type)) {
      setErrorMessage("Invalid file type. Please upload a valid Excel file.");
      return;
    }

    setIsUploading(true);
    try {
      const fileNameWithoutExt = file.name.replace(/\.[^/.]+$/, "");
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data);
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const allRows = XLSX.utils.sheet_to_json(worksheet, {
        header: 1,
        defval: "",
      }) as string[][];

      if (allRows.length < 2) {
        setErrorMessage("Excel file must have at least 2 rows (headers and data).");
        return;
      }

      const normalizeHeader = (h: string) =>
        h?.toString().toLowerCase().replace(/\*/g, "").trim();

      const hasMandatoryHeaders = (row: string[]) => {
        const norm = row.map(normalizeHeader);
        const hasNumber = norm.includes("product number");
        const hasTitle  = norm.includes("product title") || norm.includes("product name");
        const hasContent = norm.some((h) => h === "content.html" || h === "conent.html");
        return hasNumber && hasTitle && hasContent;
      };

      const firstRowHasMandatory = hasMandatoryHeaders(allRows[0]);
      const secondRowHasMandatory =
        allRows.length > 1 ? hasMandatoryHeaders(allRows[1]) : false;

      let headerRow: string[];
      let dataRows: string[][];

      if (firstRowHasMandatory) {
        headerRow = allRows[0];
        dataRows = allRows.slice(1);
      } else if (secondRowHasMandatory) {
        headerRow = allRows[1];
        dataRows = allRows.slice(2);
      } else {
        setErrorMessage("Invalid file format. Please upload Optimizely export file.");
        return;
      }

      const lowerHeaders = headerRow.map(normalizeHeader);

      const productNameIndex    = lowerHeaders.findIndex((h) => h === "product title" || h === "product name");
      const productDetailsIndex = lowerHeaders.findIndex((h) => h === "content.html" || h === "conent.html");
      const productNumberIndex  = lowerHeaders.findIndex((h) => h === "product number");
      const categoryIndex       = lowerHeaders.findIndex((h) => h === "category");
      const materialsIndex      = lowerHeaders.findIndex((h) => h === "materials");

      const jsonData = dataRows.map((row) => ({
        productName: row[productNameIndex] || "",
        productDetails: row[productDetailsIndex] || "",
        productNumber: row[productNumberIndex] || "",
        category: categoryIndex >= 0 ? row[categoryIndex] || "" : "",
        materials: materialsIndex >= 0 ? row[materialsIndex] || "" : "",
      }));

      const nonEmptyRows = jsonData.filter((row) =>
        Object.values(row).some((val) => val && String(val).trim().length > 0)
      );

      if (nonEmptyRows.length === 0) {
        setErrorMessage("No data found in file.");
        return;
      }

      const invalidRows = nonEmptyRows.filter(
        (row) =>
          !row.productName?.toString().trim() ||
          !row.productDetails?.toString().trim() ||
          !row.productNumber?.toString().trim()
      );
      if (invalidRows.length > 0) {
        setErrorMessage("Invalid file format. Please upload Optimizely export file.");
        return;
      }

      if (nonEmptyRows.length > 120) {
        setErrorMessage(
          `Too many products (${nonEmptyRows.length}). Maximum allowed is 120 per file.`
        );
        return;
      }

      const bulk_children = nonEmptyRows.map((row) => ({
        product_title: row.productName.toString().trim(),
        product_details: row.productDetails.toString().trim(),
        product_number: row.productNumber.toString().trim(),
        brand: row.category.toString().trim() || defaultBrandFilename || "",
        materials: row.materials.toString().trim(),
      }));

      const payload = {
        user_subscriptions_id: selectedHelen?.user_subscriptions_id
          ? Number(selectedHelen.user_subscriptions_id)
          : 0,
        assigned_bot_id: Number(selectedHelen?.id),
        title: fileNameWithoutExt,
        bulk_children,
      };

      await TaskService.createOptimizelyBulkTask(payload);
      showToast("Catalog tasks created successfully!", "success");
      onSuccess();
      handleClose();
    } catch (error: any) {
      setErrorMessage(error.response?.data?.error || "Failed to create tasks.");
    } finally {
      setIsUploading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-container" onClick={handleClose}>
      <div
        className="catalog-modal-content"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal header */}
        <div className="catalog-modal-header">
          <div className="catalog-modal-title-row">
            <div className="catalog-upload-icon-wrap optimizely">
              <OptimizelyLogo size={32} />
            </div>
            <div>
              <h2 className="catalog-modal-title" style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                Upload Bulk Task File
                <button
                  className="guide-help-btn"
                  onClick={(e) => { e.stopPropagation(); setIsGuideOpen(true); }}
                  title="How to generate the input Excel"
                  aria-label="Guide"
                >
                  ?
                </button>
              </h2>
              <p className="catalog-modal-subtitle">Optimizely product details</p>
            </div>
          </div>
          <img
            src="/images/cross.svg"
            alt="Close"
            className="task-icon"
            style={{ cursor: "pointer", width: "24px", height: "24px" }}
            onClick={handleClose}
          />
        </div>

        <hr className="divider" />

        {/* Upload area */}
        <div className="upload-container">
          <div className="upload-area" style={{ display: "flex", alignItems: "center" }}>
            <img
              src="/images/AddFiles.svg"
              alt="Add Files"
              className="upload-icon"
            />
            <label
              htmlFor="catalog-bulk-upload"
              className="upload-label"
              style={{ cursor: "pointer", marginLeft: "0.5rem", flex: 1 }}
            >
              Drag and drop file here, or click add files
            </label>
            <input
              ref={fileInputRef}
              id="catalog-bulk-upload"
              type="file"
              accept=".xlsx, .xls"
              onChange={handleFileChange}
              style={{ display: "none" }}
            />
            <button
              className="upload-btn"
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploading}
              style={{ marginLeft: "1rem" }}
            >
              {isUploading ? "Uploading…" : "Upload Files"}
            </button>
          </div>

          <hr className="divider-bulk-sidebar" />

          {errorMessage && (
            <p style={{ color: "red", marginTop: "1rem" }}>{errorMessage}</p>
          )}

          <p
            className="file-info"
            style={{ marginTop: "1rem", textAlign: "center" }}
          >
            * Fill your details in our Excel file template for more accurate data import
          </p>
          <button
            className="download-template"
            onClick={downloadTemplate}
            style={{ marginTop: "0.5rem" }}
          >
            Download Template
          </button>
        </div>

        {isUploading && <Loader />}
      </div>
      <BulkGuideModal isOpen={isGuideOpen} onClose={() => setIsGuideOpen(false)} />
    </div>
  );
};

// ─── Main Catalog Page ────────────────────────────────────────────────────────

const Optimizely: React.FC<{ activeTab: string }> = ({ activeTab }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isFieldMappingOpen, setIsFieldMappingOpen] = useState(false);
  const [taskHistory, setTaskHistory] = useState<TaskGroup[]>([]);
  const [isTaskLoading, setIsTaskLoading] = useState(true);
  const [isBotsLoading, setIsBotsLoading] = useState(true);
  const [botsAssignedToUser, setBotsAssignedToUser] = useState<any[]>([]);
  const [selectedHelen, setSelectedHelen] = useState<any>(null);
  const [completedCount, setCompletedCount] = useState(0);
  const [queueCount, setQueueCount] = useState(0);
  const [dailyInteractions, setDailyInteractions] = useState<string>("-");
  const [selectedFilter, setSelectedFilter] = useState<string | undefined>("");
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [defaultBrandFilename, setDefaultBrandFilename] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [userId] = useState(getUserId() || "6");
  const [tooltipTarget, setTooltipTarget] = useState<DOMRect | null>(null);
  const [tooltipContent, setTooltipContent] = useState("");
  const navigate = useNavigate();
  const role = getUserRole();

  const filterOptions = [
    { value: "", label: "All Task" },
    { value: "completed", label: "Completed" },
    { value: "onhold", label: "On hold" },
    { value: "pending", label: "Pending" },
    { value: "cancelled", label: "Cancelled" },
  ];

  useEffect(() => {
    if (activeTab === "optimizely") {
      fetchBotsAssignedToUser();
      fetchUserData();
    }
  }, [activeTab]);

  useEffect(() => {
    if (botsAssignedToUser.length > 0) setSelectedHelen(botsAssignedToUser[0]);
  }, [botsAssignedToUser]);

  useEffect(() => {
    if (activeTab === "optimizely" && selectedHelen) fetchTaskHistories();
  }, [activeTab, selectedHelen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const fetchUserData = async () => {
    try {
      const response = await TeamMembersService.getUsers(userId);
      const result = response.data;
      if (result?.user && Array.isArray(result.brandVoices) && result.brandVoices.length > 0) {
        setDefaultBrandFilename(result.brandVoices[0].filename || null);
      }
    } catch (_) {}
  };

  const fetchBotsAssignedToUser = async () => {
    try {
      setIsBotsLoading(true);
      const resp = await HelenService.getBotsAssignedToUser("active");
      if (resp?.data?.userAssignedBots) setBotsAssignedToUser(resp.data.userAssignedBots);
    } catch (_) {
    } finally {
      setIsBotsLoading(false);
    }
  };

  const fetchTaskHistories = async () => {
    if (!selectedHelen) return;
    setIsTaskLoading(true);
    try {
      const resp = await TaskService.getOptimizelyTasks({
        assigned_bot_id: Number(selectedHelen.id),
        page: 1,
        pageSize: 50,
      });
      const taskGroups: TaskGroup[] = (resp.data.data || []).map((parent: any) => ({
        parentTask: parent,
        children: parent.children || [],
      }));
      setTaskHistory(taskGroups);
      setCompletedCount(resp.data.completedTasks ?? 0);
      setQueueCount(resp.data.queuedTasks ?? 0);
      const usageLimit = selectedHelen?.usage_limit ?? 0;
      const creditUsed = resp.data.scheduledToday ?? 0;
      setDailyInteractions(`${creditUsed}/${usageLimit}`);
    } catch (_) {
      setTaskHistory([]);
      setCompletedCount(0);
      setQueueCount(0);
      if (role !== "Admin") {
        navigate("/dashboard");
        return;
      }
    } finally {
      setIsTaskLoading(false);
    }
  };

  const handleDownload = async (taskId: string) => {
    try {
      const res = await TaskService.exportOptimizelyTaskToExcel(taskId);
      const blob = new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `tasks_${taskId}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (_) {}
  };

  const handleFilterSelect = (value?: string) => {
    setSelectedFilter(value);
    setIsMenuOpen(false);
  };

  const filteredTaskHistory = taskHistory
    .map((group) => {
      const filteredChildren = group.children.filter((child) =>
        selectedFilter ? child.status === selectedFilter : true
      );
      const includeParent =
        !selectedFilter ||
        group.parentTask.status === selectedFilter ||
        filteredChildren.length > 0;
      return includeParent ? { parentTask: group.parentTask, children: filteredChildren } : null;
    })
    .filter(Boolean) as TaskGroup[];

  const filterIconSvg = (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M3.80431 2.5C3.08423 2.5 2.5 3.09405 2.5 3.82534V4.60537C2.5 5.14706 2.70599 5.66798 3.07446 6.05981L7.11258 10.3536L7.11436 10.3509C7.89393 11.149 8.33254 12.2279 8.33254 13.3528V17.1626C8.33254 17.4172 8.59891 17.5798 8.82 17.4597L11.1196 16.2066C11.4668 16.017 11.6834 15.6486 11.6834 15.2487V13.3428C11.6834 12.2242 12.1158 11.1499 12.8883 10.3536L16.9264 6.05981C17.294 5.66798 17.5 5.14706 17.5 4.60537V3.82534C17.5 3.09405 16.9167 2.5 16.1966 2.5H3.80431Z"
        stroke="#3844D1"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );

  const chevronSvg = (
    <svg
      className="chevron-icon"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d={isMenuOpen ? "M18 15L12 9L6 15" : "M6 9L12 15L18 9"}
        stroke="#3844D1"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );

  if (activeTab !== "optimizely") return null;

  if (isBotsLoading) {
    return (
      <main style={{ height: "100vh", display: "flex", justifyContent: "center", alignItems: "center" }}>
        <Loader inline={true} />
      </main>
    );
  }

  return (
    <main className="content">
      <TopBar />
      <section className="catalog-section">

        {/* Page header */}
        <div className="catalog-header">
          <div className="catalog-header-row">
            <h1 className="catalog-title">Optimizely</h1>
            <button
              className="fm-settings-btn"
              onClick={() => setIsFieldMappingOpen(true)}
              title="Field Mapping Settings"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="3" />
                <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
              </svg>
              Field Mapping
            </button>
          </div>
        </div>

        {/* Bot selection tabs */}
        <div className="task-tab">
          {botsAssignedToUser.map((helen: any, i: number) => (
            <button
              key={i}
              className={`tab-button ${helen === selectedHelen ? "active-tab" : ""}`}
              onClick={() => setSelectedHelen(helen)}
              onMouseEnter={(e) => {
                setTooltipTarget(e.currentTarget.getBoundingClientRect());
                setTooltipContent(`Daily Interactions: ${helen.usage_limit}\nPlan: ${helen.plan_name}`);
              }}
              onMouseLeave={() => {
                setTooltipTarget(null);
                setTooltipContent("");
              }}
            >
              <span className="bot-name">{helen.helen_name.replace(/_/g, " ")}</span>
            </button>
          ))}
          <Tooltip targetRect={tooltipTarget} content={tooltipContent} />
        </div>

        {/* Stats cards */}
        <div className="task-stats">
          <div className="stat-box">
            <div className="stat-card">
              <img src="/images/CompletedTasks.svg" alt="Completed Tasks" className="stat-icon" />
              <div className="stat-text">
                <p className="stat-label">Completed Tasks</p>
                <span className="stat-value">{completedCount}</span>
              </div>
            </div>
          </div>
          <div className="stat-box">
            <div className="stat-card">
              <img src="/images/TasksInqueue.svg" alt="Tasks in Queue" className="stat-icon" />
              <div className="stat-text">
                <p className="stat-label">Tasks in Queue</p>
                <span className="stat-value">{queueCount}</span>
              </div>
            </div>
          </div>
          <div className="stat-box">
            <div className="stat-card">
              <img src="/images/CreditUsages.svg" alt="Daily Interactions" className="stat-icon" />
              <div className="stat-text">
                <p className="stat-label">Daily Interactions</p>
                <span className="stat-value">{dailyInteractions}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Task list section */}
        <section className="dashboard-main">
          <section className="tasks-list">
            {!isTaskLoading && taskHistory.length === 0 ? (
              <div className="task-container">
                <div className="centered-container">
                  <img
                    src="/images/Notasks.svg"
                    alt="No Tasks"
                    className="task-icon-main"
                  />
                  <p className="Notasks">No Tasks in Queue</p>
                  <p className="Addnewtaskstext">Add a bulk task to get started!</p>
                  <div className="task-buttons">
                    <button
                      className="task-btn bulk-task-btn"
                      onClick={() => setIsModalOpen(true)}
                    >
                      + Add Bulk Task
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <>
                <div className="task-top-bar">
                  <h2 className="task-title">Task List</h2>
                  <div className="task-actions">
                    <button
                      onClick={fetchTaskHistories}
                      className="refresh-button"
                      title="Refresh Data"
                      disabled={isTaskLoading}
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="23 4 23 10 17 10"/>
                        <polyline points="1 20 1 14 7 14"/>
                        <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10"/>
                        <path d="M20.49 15A9 9 0 0 1 5.64 18.36L1 14"/>
                      </svg>
                    </button>

                    <div className="custom-dropdown-container" ref={dropdownRef}>
                      <button
                        className="tasklist-filter-button"
                        onClick={() => setIsMenuOpen(!isMenuOpen)}
                        aria-expanded={isMenuOpen}
                        aria-controls="catalog-filter-menu"
                        disabled={isTaskLoading}
                      >
                        {filterIconSvg}
                        <span>Filter by</span>
                        {chevronSvg}
                      </button>
                      {isMenuOpen && (
                        <div className="filter-menu" id="catalog-filter-menu" role="menu">
                          {filterOptions.map((option) => (
                            <div
                              key={option.value}
                              className={`filter-menu-item ${
                                selectedFilter === option.value ? "selected" : ""
                              }`}
                              onClick={() => handleFilterSelect(option.value)}
                              role="menuitem"
                            >
                              {option.label}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    <button
                      className="task-btn bulk-task-btn"
                      onClick={() => setIsModalOpen(true)}
                      disabled={isTaskLoading}
                    >
                      + Add Bulk Task
                    </button>
                  </div>
                </div>

                <TaskList
                  data={filteredTaskHistory}
                  handleDownload={handleDownload}
                  refreshChatHistories={fetchTaskHistories}
                  showProductNumber={true}
                  isLoading={isTaskLoading}
                />
              </>
            )}
          </section>
        </section>
      </section>

      <BulkUploadModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        selectedHelen={selectedHelen}
        defaultBrandFilename={defaultBrandFilename}
        onSuccess={fetchTaskHistories}
      />

      <FieldMappingModal
        isOpen={isFieldMappingOpen}
        onClose={() => setIsFieldMappingOpen(false)}
      />
    </main>
  );
};

export default Optimizely;
